import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Divider,
  Empty,
  Input,
  message,
  Modal,
  Space,
  Tag,
  Typography,
} from 'antd';
import { DownloadOutlined, UploadOutlined, TagsOutlined, DeleteOutlined } from '@ant-design/icons';
import { read, utils, writeFile } from 'xlsx';
import {
  CATEGORY_IMPORT_EXPORT_HEADERS,
  type CategoryImportDraftRow,
  parseCategoryImportMatrix,
  validateCategoryImportDrafts,
  categoryIssuesByRowIndex,
  categoryImportHasBlockingIssues,
} from '../utils/categoryImportExport';
import {
  useProductCategoriesForBusinessId,
  useSuperAdminBulkImportProductCategories,
  useSuperAdminPurgeAllProductCategoriesForBusiness,
} from '../../../hooks/useProductCategories';
import { useAuth } from '../../../contexts/AuthContext';

const { Paragraph, Text } = Typography;

type Props = {
  businessId: string;
  businessName: string;
  canUse: boolean;
  inventoryEnabled: boolean;
};

const CategoriesImportExportTabContent: React.FC<Props> = ({
  businessId,
  businessName,
  canUse,
  inventoryEnabled,
}) => {
  const { isSuperAdmin } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [draftRows, setDraftRows] = useState<CategoryImportDraftRow[]>([]);
  const [purgeModalOpen, setPurgeModalOpen] = useState(false);
  const [purgeConfirmName, setPurgeConfirmName] = useState('');
  const importMutation = useSuperAdminBulkImportProductCategories();
  const purgeCategoriesMutation = useSuperAdminPurgeAllProductCategoriesForBusiness();

  const { data: categories = [], isLoading } = useProductCategoriesForBusinessId(businessId, {
    enabled: canUse && inventoryEnabled,
  });

  const validationIssues = useMemo(
    () => validateCategoryImportDrafts(draftRows, categories),
    [draftRows, categories]
  );
  const issueMap = useMemo(() => categoryIssuesByRowIndex(validationIssues), [validationIssues]);
  const blocked =
    categoryImportHasBlockingIssues(validationIssues) || draftRows.length === 0;

  const updateRow = useCallback((rowIndex: number, name: string) => {
    setDraftRows((prev) => prev.map((r) => (r.rowIndex === rowIndex ? { ...r, name } : r)));
  }, []);

  const handleDownloadTemplate = () => {
    const ws = utils.aoa_to_sheet([Array.from(CATEGORY_IMPORT_EXPORT_HEADERS)]);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Catégories');
    writeFile(wb, `modele-categories-produits-${businessName.replace(/\s+/g, '_')}.xlsx`);
  };

  const handleExport = () => {
    if (!categories.length) {
      message.warning('Aucune catégorie active à exporter.');
      return;
    }
    const rows: string[][] = [Array.from(CATEGORY_IMPORT_EXPORT_HEADERS)];
    for (const c of categories) {
      rows.push([c.name]);
    }
    const ws = utils.aoa_to_sheet(rows);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Catégories');
    writeFile(wb, `export-categories-produits-${businessName.replace(/\s+/g, '_')}.xlsx`);
    message.success('Export généré.');
  };

  const onPickFile: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = read(buf, { type: 'array' });
      const sheetName = wb.SheetNames[0];
      if (!sheetName) {
        message.error('Fichier vide.');
        return;
      }
      const sheet = wb.Sheets[sheetName];
      const matrix = utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '' }) as unknown[][];
      const parsed = parseCategoryImportMatrix(matrix);
      setDraftRows(parsed);
      message.success(`${parsed.length} ligne(s) chargée(s). Vérifiez puis validez.`);
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : 'Lecture du fichier impossible.');
    }
  };

  const submitImport = async () => {
    if (blocked || importMutation.isPending) return;
    const names = draftRows.map((r) => r.name.trim()).filter(Boolean);
    try {
      const res = await importMutation.mutateAsync({ businessId, names });
      message.success(`${res.created} catégorie(s) créée(s).`);
      setDraftRows([]);
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : 'Erreur à l’import.');
    }
  };

  const runPurgeCategories = async () => {
    if (purgeConfirmName.trim() !== businessName.trim()) {
      message.error('Le nom saisi ne correspond pas exactement au nom du business.');
      return;
    }
    try {
      const res = await purgeCategoriesMutation.mutateAsync({ businessId });
      setPurgeModalOpen(false);
      setPurgeConfirmName('');
      message.success(
        `${res.deactivated} catégorie(s) désactivée(s).` +
          (res.skippedHasProducts > 0
            ? ` ${res.skippedHasProducts} ignorée(s) (produits actifs encore liés).`
            : '')
      );
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : 'Erreur lors de la désactivation.');
    }
  };

  if (!canUse) {
    return (
      <Card>
        <Empty description="Réservé au Super Admin ou au CSM associé à ce business." />
      </Card>
    );
  }

  if (!inventoryEnabled) {
    return (
      <Card>
        <Empty description="Le module Stock / Inventaire n’est pas activé pour ce business." />
      </Card>
    );
  }

  return (
    <div className="business-detail-tab">
      <Card title="Catégories produits — import / export Excel" loading={isLoading}>
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Exportez la liste des catégories actives, ou importez de nouvelles catégories (une colonne{' '}
          <strong>Nom</strong>). La création en masse est désactivée tant qu’une ligne comporte une erreur
          (nom vide, doublon dans le fichier ou nom déjà présent sur le business).
        </Paragraph>

        <Space wrap style={{ marginBottom: 16 }}>
          <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
            Télécharger le modèle
          </Button>
          <Button type="primary" icon={<TagsOutlined />} onClick={handleExport}>
            Exporter les catégories
          </Button>
          <Button icon={<UploadOutlined />} onClick={() => fileInputRef.current?.click()}>
            Charger un fichier
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            aria-label="Importer un fichier Excel de catégories produits"
            onChange={onPickFile}
          />
        </Space>

        {isSuperAdmin && (
          <>
            <Divider orientation="left">Super Admin</Divider>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Désactivation totale des catégories vides"
              description={
                <>
                  Désactive (<strong>isActive: false</strong>) toutes les catégories actives qui n’ont{' '}
                  <strong>aucun produit actif</strong>. Supprimez d’abord les produits (onglet Produits) si besoin.
                </>
              }
            />
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={purgeCategoriesMutation.isPending}
              onClick={() => {
                setPurgeConfirmName('');
                setPurgeModalOpen(true);
              }}
            >
              Désactiver toutes les catégories vides…
            </Button>
            <Modal
              title="Confirmer la désactivation des catégories"
              open={purgeModalOpen}
              onCancel={() => {
                setPurgeModalOpen(false);
                setPurgeConfirmName('');
              }}
              okText="Désactiver"
              okButtonProps={{ danger: true, loading: purgeCategoriesMutation.isPending }}
              onOk={runPurgeCategories}
              destroyOnClose
            >
              <Paragraph>
                Pour confirmer, saisissez exactement le nom du business : <Text strong>{businessName}</Text>
              </Paragraph>
              <Input
                value={purgeConfirmName}
                onChange={(e) => setPurgeConfirmName(e.target.value)}
                placeholder="Nom du business"
                autoComplete="off"
              />
            </Modal>
          </>
        )}

        {validationIssues.length > 0 && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message={`${validationIssues.length} ligne(s) à corriger avant import.`}
          />
        )}

        {draftRows.length === 0 ? (
          <Empty description="Aucune donnée importée. Chargez un fichier Excel ou saisissez des noms." />
        ) : (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {draftRows.map((r) => {
              const msgs = issueMap.get(r.rowIndex);
              const hasError = Boolean(msgs?.length);
              return (
                <Card
                  key={r.rowIndex}
                  size="small"
                  styles={{ body: { padding: 12 } }}
                  style={{ borderLeft: hasError ? '3px solid #ff4d4f' : undefined }}
                >
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Tag>Ligne {r.rowIndex}</Tag>
                    <Input
                      value={r.name}
                      onChange={(e) => updateRow(r.rowIndex, e.target.value)}
                      status={hasError ? 'error' : undefined}
                      placeholder="Nom de la catégorie"
                    />
                    {hasError ? (
                      <Text type="danger" style={{ fontSize: 12 }}>
                        {msgs!.join(' ')}
                      </Text>
                    ) : (
                      <Text type="success" style={{ fontSize: 12 }}>
                        OK
                      </Text>
                    )}
                  </Space>
                </Card>
              );
            })}
          </Space>
        )}

        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
          <Button onClick={() => setDraftRows([])} disabled={!draftRows.length || importMutation.isPending}>
            Vider la liste
          </Button>
          <Button
            type="primary"
            loading={importMutation.isPending}
            disabled={blocked || importMutation.isPending}
            onClick={submitImport}
          >
            Créer les catégories
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default CategoriesImportExportTabContent;
